import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth, api } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Plus, Brain, FileText, Mic, Users } from 'lucide-react';

export default function DashboardPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState({ total_sops: 0, captures_this_month: 0, team_members: 0, recent_sops: [] });
  const [loading, setLoading] = useState(true);

  useEffect(() => { fetchDashboard(); }, []);

  const fetchDashboard = async () => {
    try { const { data } = await api.get(`/api/dashboard`); setStats(data); }
    catch (err) { console.error('Dashboard fetch error:', err); }
    finally { setLoading(false); }
  };

  const statCards = [
    { label: 'Total SOPs', value: stats.total_sops, icon: FileText, color: 'text-[#AAFF00]' },
    { label: 'Captures this month', value: stats.captures_this_month, icon: Mic, color: 'text-[#3B82F6]' },
    { label: 'Team members', value: stats.team_members, icon: Users, color: 'text-[#9CA3AF]' },
  ];

  return (
    <div className="max-w-6xl mx-auto" data-testid="dashboard-page">
      <div className="mb-8">
        <h1 className="text-3xl sm:text-4xl font-semibold text-white tracking-tight">
          Welcome back, {user?.full_name?.split(' ')[0] || 'there'}
        </h1>
        <p className="mt-2 text-[#9CA3AF]">Here's what's happening with your knowledge base.</p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
        {statCards.map(({ label, value, icon: Icon, color }) => (
          <div key={label} className="bg-[#111827] rounded-lg border border-[#374151] p-5 hover:border-[#AAFF00]/40 transition-all duration-200" data-testid={`stat-${label.toLowerCase().replace(/\s/g, '-')}`}>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs font-semibold tracking-[0.05em] uppercase text-[#9CA3AF]">{label}</p>
                <p className="text-3xl font-semibold text-[#F9FAFB] mt-1">{loading ? '-' : value}</p>
              </div>
              <div className={`p-2.5 rounded-lg bg-[#1F2937] ${color}`}>
                <Icon size={20} strokeWidth={1.5} />
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Action Buttons */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-8">
        <button onClick={() => navigate('/capture')} className="flex items-center gap-4 p-6 bg-[#AAFF00] text-[#080812] rounded-lg hover:bg-[#c4ff4d] transition-all group" data-testid="new-capture-btn">
          <div className="p-3 rounded-lg bg-[#080812]/10"><Plus size={24} /></div>
          <div className="text-left">
            <p className="font-semibold text-lg">New Capture</p>
            <p className="text-sm text-[#080812]/70">Record or upload audio to create an SOP</p>
          </div>
        </button>
        <button onClick={() => navigate('/brain')} className="flex items-center gap-4 p-6 bg-[#111827] border border-[#374151] rounded-lg hover:border-[#3B82F6] transition-all group" data-testid="ask-brain-btn">
          <div className="p-3 rounded-lg bg-[#1F2937]"><Brain size={24} className="text-[#3B82F6]" /></div>
          <div className="text-left">
            <p className="font-semibold text-lg text-[#F9FAFB]">Ask the Brain</p>
            <p className="text-sm text-[#9CA3AF]">Get answers from your company's SOPs</p>
          </div>
        </button>
      </div>

      {/* Recent SOPs */}
      <div className="bg-[#111827] rounded-lg border border-[#374151]">
        <div className="p-5 border-b border-[#374151] flex items-center justify-between">
          <h2 className="text-lg font-medium text-[#AAFF00]">Recent SOPs</h2>
          <Button variant="ghost" size="sm" onClick={() => navigate('/library')} className="text-[#9CA3AF] hover:text-[#F9FAFB] hover:bg-[#1F2937]" data-testid="view-all-sops-btn">View all</Button>
        </div>
        <div className="divide-y divide-[#374151]">
          {loading ? (
            <div className="p-8 text-center text-[#9CA3AF]">Loading...</div>
          ) : stats.recent_sops.length === 0 ? (
            <div className="p-8 text-center">
              <FileText size={32} className="mx-auto text-[#374151] mb-3" />
              <p className="text-[#9CA3AF]">No SOPs yet. Create your first capture to get started.</p>
            </div>
          ) : (
            stats.recent_sops.map((sop) => (
              <button key={sop.id} onClick={() => navigate(`/library/${sop.id}`)} className="w-full flex items-center justify-between p-4 hover:bg-[#1F2937] transition-colors text-left" data-testid={`recent-sop-${sop.id}`}>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-[#F9FAFB] truncate">{sop.title}</p>
                  <p className="text-xs text-[#9CA3AF] mt-0.5">{sop.category} &middot; {new Date(sop.created_at).toLocaleDateString()}</p>
                </div>
                <span className="ml-4 px-2.5 py-1 text-xs font-medium bg-[#1F2937] text-[#9CA3AF] rounded-md border border-[#374151]">{sop.category}</span>
              </button>
            ))
          )}
        </div>
      </div>
    </div>
  );
}
